/**
 * Suspend / reinstate an account, from the Platform Usage → Users tab.
 *
 * Two stages, mirroring `RoleMutationDialog`: stage 1 confirms exactly who is
 * being acted on and collects a mandatory reason; stage 2 is the step-up
 * re-proof. Unlike the roles dialog there is no lookup step here — the target
 * is already resolved on screen, which is the whole reason this control lives
 * on the user detail card rather than in a tool of its own.
 *
 * SECURITY: submits through `stepUpFetch`, never the traced `api` client. The
 * body carries the acting admin's password and TOTP code, and `api.ts` writes
 * every request into the telemetry apiTraces ring buffer that
 * `window.fiasDebug.getState()` and the report-a-problem dialog expose.
 */

import { useEffect, useState } from 'react';
import { type AdminUserLookupResult } from '@/lib/api';
import { stepUpFetch } from '@/lib/step-up-fetch';
import { StepUpDialog, type StepUpCredentials } from '@/auth/components/StepUpDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface SuspendUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-resolved target — the user currently displayed on the Users tab. */
  user: AdminUserLookupResult;
  /** `true` suspends, `false` reinstates. Derived from the target's status. */
  suspend: boolean;
  onSuccess: () => void;
}

export function SuspendUserDialog({
  open,
  onOpenChange,
  user,
  suspend,
  onSuccess,
}: SuspendUserDialogProps) {
  const [reason, setReason] = useState('');
  const [stepUpOpen, setStepUpOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setStepUpOpen(false);
  }, [open]);

  const submit = async (creds: StepUpCredentials) => {
    await stepUpFetch(
      `/admin/users/${encodeURIComponent(user.userId)}/${suspend ? 'suspend' : 'reinstate'}`,
      {
        method: 'POST',
        body: {
          reason: reason.trim(),
          currentPassword: creds.currentPassword,
          ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
        },
      },
    );
  };

  const verb = suspend ? 'Suspend' : 'Reinstate';

  return (
    <>
      <Dialog open={open && !stepUpOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{verb} account</DialogTitle>
            <DialogDescription>
              {suspend
                ? 'The user will be unable to sign in or refresh their session. You will be asked to re-enter your password.'
                : 'The user will be able to sign in again. You will be asked to re-enter your password.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{user.username}</span>
                <Badge variant={user.status === 'active' ? 'secondary' : 'destructive'}>
                  {user.status}
                </Badge>
              </div>
              {user.displayName && <p className="text-muted-foreground">{user.displayName}</p>}
              <p className="text-muted-foreground">{user.email ?? 'no email on file'}</p>
              <p className="font-mono text-xs text-muted-foreground">{user.userId}</p>
              {user.roles.length > 0 && (
                <p className="text-muted-foreground">
                  Roles: <span className="text-foreground">{user.roles.join(', ')}</span>
                </p>
              )}
            </div>

            {/*
              What suspension does and does not do, stated where the decision is
              made. An operator reaching for this during an abuse response needs
              to know it stops the actor without destroying the evidence — and
              that it is not instant for an already-signed-in user.
            */}
            {suspend && (
              <div className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
                <p>
                  Blocks new sign-ins, session refreshes, and API-key requests. Their content, links
                  and data are left in place for review.
                </p>
                <p>
                  A session already signed in keeps working until its access token expires (up to an
                  hour). Revoke anything time-critical directly.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="suspend-reason">Reason</Label>
              <Input
                id="suspend-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ticket number or short justification"
                maxLength={500}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Recorded in the audit log alongside your account.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={suspend ? 'destructive' : 'default'}
              disabled={reason.trim().length === 0}
              onClick={() => setStepUpOpen(true)}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        title={`${verb} account`}
        description={`Confirm it’s you to ${verb.toLowerCase()} ${user.username}.`}
        confirmLabel={`${verb} account`}
        destructive={suspend}
        onConfirm={submit}
        onSuccess={() => {
          onOpenChange(false);
          onSuccess();
        }}
      />
    </>
  );
}
